import { action, extendObservable, makeObservable, computed } from 'mobx'

import DOMPurify from '@client/utils/dompurify'
import { BlockModel } from '@client/components/Block/model'
import { defaultSettings } from './constants'
import { extractUniqueColors } from '@client/utils/htmlColorExtractor'
import { captureException } from '@client/utils/sentry'

export default class TextModel extends BlockModel {
  constructor(data = {}, editor) {
    super(data)

    makeObservable(this)

    this.editor = editor

    this.settings.set({ ...defaultSettings, ...data.settings })

    extendObservable(this, {
      htmlText: DOMPurify.sanitize(data.htmlText) || '',
      color: data.color || null,
    })
  }

  // htmlText is sanitized at I/O (constructor/restore on input, serialize on
  // output) but is rendered as-is via dangerouslySetInnerHTML in the editor
  // preview. Callers of setHtmlText must pass already-trusted HTML (TinyMCE
  // output, or a transform of an already-trusted htmlText) — do not pipe raw
  // user input through here.
  @action setHtmlText = (val) => {
    this.htmlText = val
  }

  @action setColor = (val) => {
    this.color = val
  }

  @computed get allColors() {
    const s = this.settings.values
    const colors = [s.backgroundColor, s.borderColor]

    let htmlColors = []
    try {
      htmlColors = extractUniqueColors(this.htmlText)
    } catch (error) {
      console.error(error)
      captureException(error)
    }

    return Array.from(new Set(colors.concat(htmlColors).filter(Boolean)))
  }

  serialize = () => {
    return {
      type: this.type,
      htmlText: DOMPurify.sanitize(this.htmlText),
      settings: this.settings.serialize(),
    }
  }

  restore = (data) => {
    this.settings.set(data.settings)
    this.htmlText = DOMPurify.sanitize(data.htmlText) || ''
  }
}

export const NULL = new TextModel()
